import React, { useMemo, useState } from 'react';
import FundingCommissionReport from '../../components/funding/FundingCommissionReport';
import FundingExecutiveSummary from '../../components/funding/FundingExecutiveSummary';
import FundingLenderReport from '../../components/funding/FundingLenderReport';
import FundingPanel from '../../components/funding/FundingPanel';
import FundingPipelineReport from '../../components/funding/FundingPipelineReport';
import FundingReadinessReport from '../../components/funding/FundingReadinessReport';
import FundingReportExportButton from '../../components/funding/FundingReportExportButton';
import FundingReportFilters from '../../components/funding/FundingReportFilters';
import FundingSectionPage from '../../components/funding/FundingSectionPage';
import { useFundingWorkspace } from '../../context/FundingWorkspaceContext';
import { useFundingReports } from '../../hooks/funding/useFundingReports';
import { downloadCsvExport } from '../../services/funding/fundingReportsService';

const TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'pipeline', label: 'Pipeline' },
  { id: 'commissions', label: 'Commissions' },
  { id: 'readiness', label: 'Readiness' },
  { id: 'lenders', label: 'Lenders' },
];

const FundingReportsPage = () => {
  const { workspaceAgencyId, isPlatformContext, requiresAgencySelection } = useFundingWorkspace();
  const [tab, setTab] = useState('summary');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [status, setStatus] = useState('');
  const [lenderType, setLenderType] = useState('');
  const [productType, setProductType] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [readinessTier, setReadinessTier] = useState('');
  const [commissionStatus, setCommissionStatus] = useState('');
  const [stage, setStage] = useState('');

  const agencyId = isPlatformContext ? workspaceAgencyId || undefined : workspaceAgencyId;

  const filters = useMemo(() => ({
    agencyId,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    status: status || undefined,
    lenderType: lenderType || undefined,
    productType: productType || undefined,
    assignedUserId: assignedUserId || undefined,
    readinessTier: readinessTier || undefined,
    commissionStatus: commissionStatus || undefined,
    stage: stage || undefined,
  }), [agencyId, dateFrom, dateTo, status, lenderType, productType, assignedUserId, readinessTier, commissionStatus, stage]);

  const { data, loading, exporting, error, exportReport } = useFundingReports({
    tab,
    filters,
    autoLoad: Boolean(agencyId) || isPlatformContext,
  });

  const handleExport = async () => {
    try {
      const payload = await exportReport(tab);
      downloadCsvExport(payload);
    } catch {
      // surfaced in hook
    }
  };

  const renderTab = () => {
    switch (tab) {
      case 'pipeline':
        return <FundingPipelineReport data={data} loading={loading} />;
      case 'commissions':
        return <FundingCommissionReport data={data} loading={loading} />;
      case 'readiness':
        return <FundingReadinessReport data={data} loading={loading} />;
      case 'lenders':
        return <FundingLenderReport data={data} loading={loading} />;
      default:
        return <FundingExecutiveSummary data={data} loading={loading} />;
    }
  };

  return (
    <FundingSectionPage
      title="Reports"
      subtitle="Estimated funding performance across clients, pipeline, commissions, readiness, and lender match."
      activeStage="funded"
      actions={<FundingReportExportButton onExport={handleExport} exporting={exporting} />}
    >
      <FundingPanel title="Report filters" subtitle="Date range and workspace filters">
        {requiresAgencySelection ? (
          <p className="mb-4 text-sm text-[var(--fg-muted)]">Select an agency in the header to load reports.</p>
        ) : null}
        <FundingReportFilters
          dateFrom={dateFrom}
          dateTo={dateTo}
          status={status}
          lenderType={lenderType}
          productType={productType}
          assignedUserId={assignedUserId}
          readinessTier={readinessTier}
          commissionStatus={commissionStatus}
          stage={stage}
          showReadinessTier={tab === 'readiness'}
          showCommissionStatus={tab === 'commissions'}
          showStage={tab === 'pipeline'}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onStatusChange={setStatus}
          onLenderTypeChange={setLenderType}
          onProductTypeChange={setProductType}
          onAssignedUserChange={setAssignedUserId}
          onReadinessTierChange={setReadinessTier}
          onCommissionStatusChange={setCommissionStatus}
          onStageChange={setStage}
        />
      </FundingPanel>

      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`rounded-xl px-4 py-2 text-sm font-medium ${
              tab === item.id
                ? 'bg-[var(--fg-accent)] text-white'
                : 'border border-[var(--fg-line)] bg-white text-[var(--fg-muted)] dark:bg-slate-950/20'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {error ? (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
          {error}
        </div>
      ) : null}

      <FundingPanel title={TABS.find((item) => item.id === tab)?.label} subtitle="Estimated metrics for the selected range">
        {renderTab()}
      </FundingPanel>
    </FundingSectionPage>
  );
};

export default FundingReportsPage;
